/**
 * Token Manager Component
 *
 * This component provides an interface for managing design tokens,
 * including generation from palettes, organization, and export.
 */

import { useState, useCallback, useEffect } from "react";
import {
  Palette,
  Settings,
  Download,
  Plus,
  Search,
  Trash2,
  Copy,
  Grid,
  List,
} from "lucide-react";
import { PaletteRecord } from "../lib/database";
import {
  tokenEngineService,
  TokenExportOptions,
} from "../lib/tokenEngineIntegration";
import { TokenSet, TokenGenerationOptions } from "../types/designTokens";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { useToolState } from "../contexts/ToolStateContext";

interface TokenSetWithMetadata extends TokenSet {
  id: string;
  name: string;
  createdAt: number;
  modifiedAt: number;
}

interface TokenManagerProps {
  className?: string;
}

export function TokenManager({ className }: TokenManagerProps) {
  const { getToolState, setToolState } = useToolState();
  const persisted = getToolState('tokens');

  const [tokenSets, setTokenSets] = useState<TokenSetWithMetadata[]>(
    (persisted?.tokenSets as TokenSetWithMetadata[] | undefined) ?? [],
  );
  const [selectedTokenSet, setSelectedTokenSet] =
    useState<TokenSetWithMetadata | null>(() => {
      const id = persisted?.selectedTokenSetId;
      if (!id) return null;
      const sets = (persisted?.tokenSets as TokenSetWithMetadata[] | undefined) ?? [];
      return sets.find((ts) => ts.id === id) ?? null;
    });
  const [searchQuery, setSearchQuery] = useState(
    persisted?.searchQuery ?? "",
  );
  const [viewMode, setViewMode] = useState<"grid" | "list">(
    persisted?.viewMode ?? "grid",
  );
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // State for available palettes
  const [availablePalettes, setAvailablePalettes] = useState<PaletteRecord[]>(
    [],
  );
  const [isLoadingPalettes, setIsLoadingPalettes] = useState(false);

  // Load available palettes on component mount
  useEffect(() => {
    const loadPalettes = async () => {
      setIsLoadingPalettes(true);
      try {
        const palettes = await tokenEngineService.getAvailablePalettes();
        setAvailablePalettes(palettes);
      } catch (error) {
        console.error("Failed to load palettes:", error);
      } finally {
        setIsLoadingPalettes(false);
      }
    };

    loadPalettes();
  }, []);

  useEffect(() => {
    setToolState('tokens', {
      tokenSets,
      selectedTokenSetId: selectedTokenSet?.id ?? null,
      viewMode,
      searchQuery,
    });
  }, [tokenSets, selectedTokenSet, viewMode, searchQuery, setToolState]);

  const generateTokensFromPalette = useCallback(
    async (
      paletteId: string,
      options: Partial<TokenGenerationOptions> = {},
    ) => {
      setIsGenerating(true);
      try {
        setError(null);
        const result = await tokenEngineService.generateTokensFromPalette(
          paletteId,
          options,
        );

        if (!result.validation.valid) {
          setError(`Token validation failed: ${result.validation.errors.map(e => e.message).join(", ")}`);
          return;
        }

        const newTokenSet: TokenSetWithMetadata = {
          ...result.tokenSet,
          id: `tokens-${Date.now()}`,
          name: result.tokenSet.$metadata?.name || "Generated Tokens",
          createdAt: result.generatedAt,
          modifiedAt: result.generatedAt,
        };

        setTokenSets((prev) => [...prev, newTokenSet]);
        setSelectedTokenSet(newTokenSet);
      } catch (error) {
        setError(`Failed to generate tokens: ${error instanceof Error ? error.message : String(error)}`);
      } finally {
        setIsGenerating(false);
      }
    },
    [],
  );

  const exportTokenSet = useCallback(
    async (tokenSet: TokenSetWithMetadata, platform: string) => {
      try {
        const exportOptions: TokenExportOptions = {
          platform: platform as TokenExportOptions["platform"],
          includeMetadata: true,
          includeComments: true,
          minify: false,
          namingConvention: "kebab-case",
        };

        const result = await tokenEngineService.exportTokens(
          tokenSet,
          exportOptions,
        );

        // Create download
        const blob = new Blob([result.content], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = result.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (error) {
        setError(`Failed to export tokens: ${error instanceof Error ? error.message : String(error)}`);
      }
    },
    [],
  );

  const deleteTokenSet = useCallback(
    (id: string) => {
      setTokenSets((prev) => prev.filter((ts) => ts.id !== id));
      if (selectedTokenSet?.id === id) {
        setSelectedTokenSet(null);
      }
    },
    [selectedTokenSet],
  );

  const filteredTokenSets = tokenSets.filter(
    (tokenSet) =>
      tokenSet.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tokenSet.$metadata?.description
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase()),
  );

  return (
    <div className={`space-y-6 ${className || ""}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            Design Tokens
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Generate, manage, and export design tokens from your color palettes
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center border rounded-md">
            <Button
              variant={viewMode === "grid" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("grid")}
              className="rounded-r-none"
            >
              <Grid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === "list" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("list")}
              className="rounded-l-none"
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex items-center gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search token sets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {error && (
        <div className="flex items-center justify-between gap-4 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setError(null)}
            className="shrink-0"
          >
            Dismiss
          </Button>
        </div>
      )}

      {/* Generate Tokens Section */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <Plus className="w-5 h-5 mr-2" />
          Generate Tokens from Palettes
        </h2>
        {isLoadingPalettes ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600 dark:text-gray-400">
              Loading palettes...
            </span>
          </div>
        ) : availablePalettes.length === 0 ? (
          <div className="text-center py-8">
            <Palette className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              No Palettes Available
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Create some color palettes first to generate design tokens
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {availablePalettes.map((palette) => (
              <Card key={palette.id} className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium">{palette.name}</h3>
                  <div className="flex gap-1">
                    {palette.colors.slice(0, 4).map((color, index) => (
                      <div
                        key={index}
                        title={color}
                        className="w-6 h-6 rounded border border-gray-200 dark:border-gray-700 cursor-default"
                        style={{ backgroundColor: color }}
                      />
                    ))}
                    {palette.colors.length > 4 && (
                      <div className="w-6 h-6 rounded border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-xs">
                        +{palette.colors.length - 4}
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  {palette.colors.length} colors
                  {palette.tags && palette.tags.length > 0 && (
                    <span className="ml-2">
                      • {palette.tags.slice(0, 3).join(", ")}
                      {palette.tags.length > 3 &&
                        ` +${palette.tags.length - 3}`}
                    </span>
                  )}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-500 mb-3">
                  Modified:{" "}
                  {new Date(
                    palette.modified ?? Date.now(),
                  ).toLocaleDateString()}
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() =>
                      palette.id && generateTokensFromPalette(palette.id)
                    }
                    disabled={isGenerating}
                    className="flex-1"
                  >
                    <Palette className="w-4 h-4 mr-2" />
                    Generate
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    title="Generate with semantic naming conventions"
                    aria-label="Generate tokens with semantic naming"
                    onClick={() =>
                      palette.id &&
                      generateTokensFromPalette(palette.id, {
                        namingConvention: "semantic",
                        includeSemanticTokens: true,
                      })
                    }
                    disabled={isGenerating}
                  >
                    <Settings className="w-4 h-4" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </Card>

      {/* Token Sets List */}
      {filteredTokenSets.length > 0 && (
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Generated Token Sets</h2>
          <div
            className={
              viewMode === "grid"
                ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                : "space-y-4"
            }
          >
            {filteredTokenSets.map((tokenSet) => (
              <Card
                key={tokenSet.id}
                className={`p-4 cursor-pointer transition-all hover:shadow-md ${
                  selectedTokenSet?.id === tokenSet.id
                    ? "ring-2 ring-blue-500"
                    : ""
                }`}
                onClick={() => setSelectedTokenSet(tokenSet)}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium">{tokenSet.name}</h3>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      title="Download as JSON"
                      aria-label="Download token set as JSON"
                      onClick={(e) => {
                        e.stopPropagation();
                        exportTokenSet(tokenSet, "json");
                      }}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      title="Export as CSS"
                      aria-label="Export token set as CSS"
                      onClick={(e) => {
                        e.stopPropagation();
                        exportTokenSet(tokenSet, "css");
                      }}
                    >
                      CSS
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      title="Remove token set"
                      aria-label="Remove token set"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteTokenSet(tokenSet.id);
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  {tokenSet.$metadata?.description}
                </p>
                <div className="text-xs text-gray-500 dark:text-gray-500">
                  Created: {new Date(tokenSet.createdAt).toLocaleDateString()}
                </div>
              </Card>
            ))}
          </div>
        </Card>
      )}

      {/* Selected Token Set Details */}
      {selectedTokenSet && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Token Set Details</h2>
            <div className="flex items-center gap-2">
              {selectedTokenSet.$metadata?.version && (
                <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded-md font-mono">
                  v{selectedTokenSet.$metadata.version}
                </span>
              )}
              <span className="text-xs px-2 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-md">
                {selectedTokenSet.tokens
                  ? `${Object.keys(selectedTokenSet.tokens).length} tokens`
                  : "0 tokens"}
              </span>
            </div>
          </div>
          <div className="space-y-4">
            <details>
              <summary className="font-medium mb-2 cursor-pointer text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200">
                Metadata
              </summary>
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-md">
                <pre className="text-sm overflow-x-auto">
                  {JSON.stringify(selectedTokenSet.$metadata, null, 2)}
                </pre>
              </div>
            </details>

            <div>
              <h3 className="font-medium mb-2">Tokens</h3>
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-md max-h-96 overflow-y-auto">
                <div className="space-y-1">
                  {selectedTokenSet.tokens &&
                    Object.entries(selectedTokenSet.tokens).map(
                      ([name, token]) => {
                        const hasValue =
                          token != null &&
                          typeof token === "object" &&
                          "$value" in token;

                        if (!hasValue) {
                          return (
                            <div
                              key={name}
                              className="flex items-center gap-2 py-1.5 px-2 rounded text-gray-400 dark:text-gray-600"
                            >
                              <code className="text-sm font-mono">{name}</code>
                              <span className="text-xs italic">
                                (nested group — expand not yet supported)
                              </span>
                            </div>
                          );
                        }

                        const typedToken = token as {
                          $type?: string;
                          $value: unknown;
                        };
                        const value = String(typedToken.$value);
                        const isColor =
                          typedToken.$type === "color" ||
                          (typeof value === "string" &&
                            /^#([0-9a-f]{3,8})$/i.test(value));

                        return (
                          <div
                            key={name}
                            className="group flex items-center gap-2 py-1.5 px-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            {isColor && (
                              <div
                                className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 shrink-0"
                                style={{ backgroundColor: value }}
                              />
                            )}
                            <code className="text-sm font-mono min-w-0 truncate">
                              {name}
                            </code>
                            {typedToken.$type && (
                              <span className="text-xs px-1.5 py-0.5 bg-gray-200 dark:bg-gray-600 rounded font-mono shrink-0">
                                {typedToken.$type}
                              </span>
                            )}
                            <span className="text-sm text-gray-600 dark:text-gray-400 truncate">
                              {value}
                            </span>
                            <button
                              type="button"
                              className="opacity-0 group-hover:opacity-100 focus:opacity-100 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 shrink-0 ml-auto"
                              onClick={() =>
                                navigator.clipboard.writeText(value)
                              }
                              aria-label={`Copy value of ${name}`}
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      },
                    )}
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Empty State */}
      {tokenSets.length === 0 && !isGenerating && (
        <Card className="p-12 text-center">
          <Palette className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            No Token Sets Yet
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Generate your first token set from one of your color palettes above
          </p>
          <div className="flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={() => {
                const sampleTokenSet: TokenSetWithMetadata = {
                  id: `tokens-sample-${Date.now()}`,
                  name: "Sample Brand",
                  createdAt: Date.now(),
                  modifiedAt: Date.now(),
                  $metadata: {
                    name: "Sample Brand",
                    description: "A sample set of brand color tokens",
                  },
                  tokens: {
                    primary: { $type: "color", $value: "#3b82f6" },
                    secondary: { $type: "color", $value: "#10b981" },
                    accent: { $type: "color", $value: "#f59e0b" },
                    neutral: { $type: "color", $value: "#6b7280" },
                    danger: { $type: "color", $value: "#ef4444" },
                  },
                };
                setTokenSets((prev) => [...prev, sampleTokenSet]);
                setSelectedTokenSet(sampleTokenSet);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
            >
              Load sample token set
            </button>
            <a
              href="/education"
              className="text-xs text-gray-500 dark:text-gray-400 underline hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            >
              Learn what tokens are →
            </a>
          </div>
        </Card>
      )}

      {tokenSets.length > 0 && filteredTokenSets.length === 0 && !isGenerating && (
        <Card className="p-12 text-center">
          <Search className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            No matching token sets
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            No token sets match &ldquo;{searchQuery}&rdquo;
          </p>
        </Card>
      )}
    </div>
  );
}
